import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useToast } from '../lib/store';
import type {
  BloodlineDetail,
  BloodlinesResponse,
  HuntResponse,
  LeaderboardResponse,
  MasteryResponse,
} from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel, Tabs } from '../components/ui';
import { formatNumber } from '../lib/format';

/**
 * The Breeder pages: the Shiny hunt, Species Mastery, and registered Bloodlines.
 *
 * One page with tabs rather than three rail entries, because these are three
 * views of the same long-running project and a breeder moves between them
 * constantly. The rail is already nine sections (see `NAV_SECTIONS`), and three
 * more would have made the thing it exists to prevent — a wall of flat links
 * where nothing reads as important.
 */
export function BreederPage() {
  const [tab, setTab] = useState('hunt');

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Breeder</h1>
          <div className="page-head__sub">
            Your hunt, what you have mastered, and the lines you keep.
          </div>
        </div>
        <Link className="btn btn--primary" to="/nursery">
          Nursery
        </Link>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'hunt', label: 'Shiny Hunt' },
          { value: 'mastery', label: 'Mastery' },
          { value: 'bloodlines', label: 'Bloodlines' },
          { value: 'records', label: 'Records' },
        ]}
      />

      {tab === 'hunt' && <HuntTab />}
      {tab === 'mastery' && <MasteryTab />}
      {tab === 'bloodlines' && <BloodlinesTab />}
      {tab === 'records' && <RecordsTab />}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Shiny hunt — design PART 9
   ------------------------------------------------------------------------- */

function HuntTab() {
  const { push } = useToast();
  const state = useAsync<HuntResponse>(() => api.get('/breeder/hunt'));
  const [busy, setBusy] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Checking the hunt…</p>;

  const { hunt, odds, advanced, history } = state.data;

  async function stop() {
    setBusy(true);
    try {
      await api.post('/breeder/hunt/stop', {});
      push('success', 'Hunt paused. Your progress is kept.', '✦');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not stop the hunt.');
    } finally {
      setBusy(false);
    }
  }

  if (!hunt) {
    return (
      <Panel title="✦ Shiny Hunt">
        <EmptyState title="No hunt running">
          Pick a species from its Pokédex page to start hunting. Eggs from that whole evolutionary
          family count towards the chain.
        </EmptyState>
      </Panel>
    );
  }

  return (
    <div className="stack">
      <Panel title="✦ Shiny Hunt" hint={hunt.species.name}>
        <div className="stack-sm">
          <div className="row" style={{ gap: 'var(--gap-sm)', alignItems: 'center' }}>
            <img className="pixel" src={hunt.species.sprite} alt="" width={64} height={64} />
            <div className="stack-sm" style={{ gap: 2, flex: 1 }}>
              <div className="mono" style={{ fontSize: 20 }}>
                Chain {formatNumber(hunt.chain)}
              </div>
              {/* The band, never the percentage — design PART 47. */}
              {odds && (
                <div className="tiny">
                  <strong>{odds.label}</strong> — {odds.blurb}
                </div>
              )}
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8 }}>
            <Stat label="This hunt" value={formatNumber(hunt.eggsThisHunt)} />
            <Stat label="Longest chain" value={formatNumber(hunt.longestChain)} />
            <Stat label="Lifetime eggs" value={formatNumber(hunt.totalEggs)} />
            <Stat label="Shinies found" value={formatNumber(hunt.shiniesFound)} />
            {hunt.lastShinyAtEgg != null && (
              <Stat label="Last shiny at" value={`egg ${formatNumber(hunt.lastShinyAtEgg)}`} />
            )}
          </div>

          {/* Bad-luck protection, shown as reassurance rather than as a number
              to optimise against. */}
          {advanced && advanced.pityAt > 0 && (
            <div className="stack-sm">
              <Bar ratio={Math.min(1, hunt.sinceLastShiny / advanced.pityAt)} variant="egg" />
              <div className="tiny muted">
                {advanced.pityActive
                  ? 'It has been a long wait. Luck is being nudged your way.'
                  : 'Long droughts are protected against. Keep going.'}
              </div>
            </div>
          )}

          <Button size="sm" variant="ghost" loading={busy} onClick={() => void stop()}>
            Pause this hunt (progress is kept)
          </Button>
        </div>
      </Panel>

      {history.length > 0 && (
        <Panel title="◈ Past hunts" hint="Lifetime totals are never reset">
          <div className="stack-sm">
            {history.map((row) => (
              <div key={row.speciesId} className="row row--between tiny">
                <span>
                  {row.speciesName}
                  {row.isActive && <span className="chip chip--sm" style={{ marginLeft: 6 }}>active</span>}
                </span>
                <span className="muted">
                  {formatNumber(row.totalEggs)} eggs · best chain {formatNumber(row.longestChain)} ·{' '}
                  {row.shiniesFound} shiny
                </span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Species Mastery — design PART 10
   ------------------------------------------------------------------------- */

function MasteryTab() {
  const state = useAsync<MasteryResponse>(() => api.get('/breeder/mastery'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Reading your notes…</p>;

  const masteries = state.data.masteries.filter((row) => row.eggsBred + row.eggsHatched > 0);

  if (masteries.length === 0) {
    return (
      <Panel title="◈ Species Mastery">
        <EmptyState title="Nothing mastered yet">
          Breed and hatch eggs of a family to build mastery with it. The rewards are prestige and
          small conveniences — never better odds.
        </EmptyState>
      </Panel>
    );
  }

  return (
    <Panel title="◈ Species Mastery" hint={`${masteries.length} families`}>
      <div className="stack-sm">
        {masteries.map((row) => {
          const into = row.nextGoal ? row.score / row.nextGoal : 1;
          return (
            <div key={row.speciesId} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
              <div className="row" style={{ gap: 'var(--gap-sm)', alignItems: 'center' }}>
                <img className="pixel" src={row.sprite} alt="" width={40} height={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row row--between">
                    <strong className="tiny">{row.speciesName}</strong>
                    <span className="chip chip--sm">{row.levelName}</span>
                  </div>
                  <Bar ratio={Math.min(1, into)} variant="exp" />
                  <div className="row row--between tiny muted">
                    <span>
                      {formatNumber(row.eggsBred)} bred · {formatNumber(row.eggsHatched)} hatched
                    </span>
                    <span>
                      {row.nextGoal
                        ? `${formatNumber(row.score)} / ${formatNumber(row.nextGoal)} to ${row.nextName}`
                        : 'Grandmaster'}
                    </span>
                  </div>
                </div>
              </div>
              {(row.shiniesHatched > 0 || row.highestGeneration > 0) && (
                <div className="row row--wrap tiny muted" style={{ gap: 6, marginTop: 4 }}>
                  {row.shiniesHatched > 0 && <span>✦ {row.shiniesHatched} shiny</span>}
                  {row.highestGeneration > 0 && <span>Gen {row.highestGeneration} best</span>}
                  {row.longestChain > 0 && <span>Chain {formatNumber(row.longestChain)}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------
   Bloodlines — design PART 7
   ------------------------------------------------------------------------- */

function BloodlinesTab() {
  const { push } = useToast();
  const state = useAsync<BloodlinesResponse>(() => api.get('/breeder/bloodlines'));
  const [founderId, setFounderId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <p className="tiny muted">Looking up your lines…</p>;

  const { rules, bloodlines } = state.data;
  const active = bloodlines.filter((line): line is BloodlineDetail => line != null);

  async function register() {
    setBusy(true);
    try {
      await api.post('/breeder/bloodlines', { founderPokemonId: founderId.trim(), name: name.trim() });
      push('success', 'Your line is registered.', '❦');
      setName('');
      setFounderId('');
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not register that line.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      {active.length > 0 && (
        <Panel title="❦ Your lines" hint={`${active.length} / ${rules.maxPerPlayer}`}>
          <div className="stack-sm">
            {active.map((line) => (
              <div key={line.id} className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
                <div className="row row--between">
                  <strong>{line.name}</strong>
                  <span className="chip chip--sm">{line.family.name}</span>
                </div>
                {line.motto && <div className="tiny muted">“{line.motto}”</div>}
                <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 6, marginTop: 6 }}>
                  <Stat label="Descendants" value={formatNumber(line.totalDescendants)} />
                  <Stat label="Still around" value={formatNumber(line.livingDescendants)} />
                  <Stat label="Shinies" value={formatNumber(line.shiniesProduced)} />
                  <Stat label="Best gen" value={String(line.highestGeneration)} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title="❦ Register a line" raised={false}>
        <p className="tiny muted">
          A line needs {formatNumber(rules.minEggs)} eggs bred of that family before you can name it.
          Descendants inherit the name automatically. You can keep {rules.maxPerPlayer} at once.
        </p>
        <div className="stack-sm" style={{ marginTop: 'var(--gap-sm)' }}>
          <input
            className="input"
            placeholder="Founder Pokémon ID"
            value={founderId}
            onChange={(event) => setFounderId(event.target.value)}
          />
          <input
            className="input"
            placeholder="Line name, e.g. Moonpetal"
            maxLength={32}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Button
            size="sm"
            variant="primary"
            loading={busy}
            disabled={!founderId.trim() || name.trim().length < 3}
            onClick={() => void register()}
          >
            Register
          </Button>
        </div>
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Records — design PART 40
   ------------------------------------------------------------------------- */

const BOARDS = [
  { key: 'eggs', label: 'Most eggs produced' },
  { key: 'shinies', label: 'Most shinies bred' },
  { key: 'generation', label: 'Deepest lineage' },
  { key: 'pairs', label: 'Greatest pairing' },
] as const;

/**
 * Breeding leaderboards.
 *
 * Living Trainers are excluded server-side via `HUMAN_ONLY` — an NPC owns real
 * Groves and acts through the real interaction code, so any query that surfaces
 * *competitors* has to filter them out or Brock ends up top of the egg table.
 */
function RecordsTab() {
  const [board, setBoard] = useState<string>('eggs');
  const state = useAsync<LeaderboardResponse>(
    () => api.get(`/breeder/leaderboard`, { board }),
    [board],
  );

  return (
    <Panel title="▤ Breeding records">
      <div className="row row--wrap" style={{ gap: 4, marginBottom: 'var(--gap-sm)' }}>
        {BOARDS.map((entry) => (
          <Button
            key={entry.key}
            size="sm"
            variant={board === entry.key ? 'special' : 'default'}
            onClick={() => setBoard(entry.key)}
          >
            {entry.label}
          </Button>
        ))}
      </div>

      {state.error && <ErrorState message={state.error} onRetry={state.reload} />}
      {!state.data ? (
        <p className="tiny muted">Counting…</p>
      ) : state.data.rows.length === 0 ? (
        <EmptyState title="Nobody yet">
          Nothing has been bred on this board. Somebody has to be first.
        </EmptyState>
      ) : (
        <div className="stack-sm">
          {state.data.rows.map((row) => (
            <div key={`${row.userId}-${row.rank}`} className="row row--between tiny">
              <span>
                <span className="mono muted" style={{ marginRight: 6 }}>
                  #{row.rank}
                </span>
                <Link to={`/trainer/${row.username}`}>{row.username}</Link>
              </span>
              <span>
                <strong className="mono">{formatNumber(row.value)}</strong>{' '}
                <span className="muted">{row.detail}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack-sm" style={{ gap: 0 }}>
      <div className="tiny muted">{label}</div>
      <div className="mono">{value}</div>
    </div>
  );
}
